import { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router';
import {
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  UsersIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  ArrowRightStartOnRectangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { signOutUser } from '@/services/auth';

interface UserMenuProps {
  userName: string | undefined;
  userEmail: string | undefined;
}

const workspaceLinks = [
  { name: 'Approvals', to: '/approvals', icon: ClipboardDocumentCheckIcon },
  { name: 'Reports', to: '/reports', icon: ChartBarIcon },
] as const;

const adminLinks = [
  { name: 'Users', to: '/users', icon: UsersIcon },
  { name: 'Invoices', to: '/invoices', icon: DocumentTextIcon },
] as const;

function menuLinkClasses({ isActive }: { isActive: boolean }): string {
  const base =
    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors w-full';
  return isActive
    ? `${base} bg-primary-50 text-primary-700 font-medium dark:bg-primary-950 dark:text-primary-300`
    : `${base} text-surface-600 hover:bg-surface-50 hover:text-surface-900 dark:text-surface-400 dark:hover:bg-surface-800 dark:hover:text-surface-200`;
}

export function UserMenu({ userName, userEmail }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  const initials = userName?.charAt(0)?.toUpperCase() ?? '?';

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-surface-100 dark:hover:bg-surface-800"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-sm font-semibold dark:bg-primary-900 dark:text-primary-300">
          {initials}
        </div>
        <ChevronDownIcon
          className={`h-4 w-4 text-surface-500 transition-transform duration-200 dark:text-surface-400 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-64 origin-top-right rounded-xl border border-surface-200 bg-white py-2 shadow-lg dark:border-surface-700 dark:bg-surface-900"
          role="menu"
        >
          {/* User info */}
          <div className="px-4 py-3 border-b border-surface-100 dark:border-surface-800">
            <p className="truncate text-sm font-medium text-surface-900 dark:text-surface-100">
              {userName}
            </p>
            <p className="truncate text-xs text-surface-500 dark:text-surface-400">
              {userEmail}
            </p>
          </div>

          {/* Workspace links */}
          <div className="px-2 pt-2 pb-1">
            <p className="px-3 pb-1 text-xs font-medium uppercase tracking-wider text-surface-400 dark:text-surface-500">
              Workspace
            </p>
            {workspaceLinks.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={menuLinkClasses}
                onClick={() => setOpen(false)}
                role="menuitem"
              >
                <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                {item.name}
              </NavLink>
            ))}
          </div>

          {/* Admin links */}
          <div className="px-2 pt-2 pb-1 border-t border-surface-100 dark:border-surface-800">
            <p className="px-3 pb-1 text-xs font-medium uppercase tracking-wider text-surface-400 dark:text-surface-500">
              Administration
            </p>
            {adminLinks.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={menuLinkClasses}
                onClick={() => setOpen(false)}
                role="menuitem"
              >
                <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                {item.name}
              </NavLink>
            ))}
          </div>

          {/* Settings & Sign out */}
          <div className="px-2 pt-1 pb-1 border-t border-surface-100 dark:border-surface-800">
            <NavLink
              to="/settings"
              className={menuLinkClasses}
              onClick={() => setOpen(false)}
              role="menuitem"
            >
              <Cog6ToothIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              Settings
            </NavLink>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                signOutUser();
              }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-surface-600 transition-colors hover:bg-red-50 hover:text-red-700 dark:text-surface-400 dark:hover:bg-red-950 dark:hover:text-red-400"
              role="menuitem"
            >
              <ArrowRightStartOnRectangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
